<template>
    <div id="custom-file-dropzone" @drop="handleDropFiles">
        <div :class="['files-preview-container', files.length > 0 && 'expanded']" v-if="previews.length > 0">
            <div class="file-name-preview" v-for="(preview, i) in previews" :key="i">
                <p class="text-10 text-weight-bold">
                    {{ files[i].name }}
                </p>
                <div class="remove-icon" @click="() => removeFile(i)">
                    <icon name="times" scale="0.8" color="var(--light)" />
                </div>
            </div>
        </div>
        <div
            :class="['drop-container', files.length > 0 && 'minimize']"
            @click="triggerFileInput"
            v-if="this.multiple || (!this.multiple && files.length == 0)"
        >
            <b-form-file @input="processFiles" :multiple="multiple" ref="files-input" class="d-none" :accept="accept ? accept : '*'"></b-form-file>

            <p v-if="!customMessage" class="text-12">
                {{ $t('Components_Utils_CustomFileDropzone_1') }} <br />
                <b>{{ $t('Components_Utils_CustomFileDropzone_2') }}</b>
            </p>

            <p class="text-12" v-else>
                <slot></slot>
            </p>

            <icon name="plus" scale="1" color="var(--light)" />
        </div>

        <p class="text-10 text-danger" v-if="filesExceededMaxSize">
            {{ $t('Components_Utils_CustomFileDropzone_3') }}
        </p>
    </div>
</template>
<script>
// ================
// Icons
// ================
import 'vue-awesome/icons/plus'
import 'vue-awesome/icons/file-alt'

import CINUtils from '@/services/Utils'

/**
 * Summary.
 * Use this component as an input for multiple files.
 *
 * @param {String} accept - accepted file types separated by comma. Ex: ".pdf, .png, .jpg"
 * @param {String} validationMessage
 *
 * @param {Number} maxFileSize - in MB. Use -1 to avoid this validation
 *
 * @param {Boolean} multiple - accept multiple files
 * @param {Function} validator - a function to validate when new files are added. Must return a boolean.
 * @param {Array} value - an initial array of files
 *
 * @event v-model/input - Emitted when files change
 */
export default {
    name: 'CustomFileDropzone',
    watch: {
        files: function () {
            this.$emit('input', this.files)
        },
        value: {
            handler() {
                if (this.value && this.value.length) {
                    this.files = this.value
                    this.generateFilesPreviews()
                }
            },
            deep: true,
        },
    },
    props: {
        accept: String,
        validationMessage: String,
        typeErrorMessage: String,

        maxFileSize: {
            type: Number,
            default: 20,
        },

        customMessage: {
            type: Boolean,
            default: false,
        },

        multiple: Boolean,
        showPreview: Boolean,
        validator: Function,
        value: Array,
    },
    data() {
        return {
            files: [],
            previews: [],
            filesExceededMaxSize: false,
        }
    },
    methods: {
        handleDropFiles(e) {
            if (this.multiple) {
                let files = []
                for (let x = 0; x < e.dataTransfer.files.length; x++) {
                    files.push(e.dataTransfer.files.item(x))
                }

                this.processFiles(files)
            } else {
                this.processFiles(e.dataTransfer.files.item(0))
            }
        },

        triggerFileInput() {
            this.$refs['files-input'].$el.children[0].click()
        },

        validateFilesMaxSize(files) {
            if (this.maxFileSize != -1) {
                const maxSizeInBytes = this.maxFileSize * 1000000
                return !files.some((file) => file.size > maxSizeInBytes)
            } else return true
        },

        processFiles(filesArgs) {
            if (filesArgs) {
                let files = this.multiple ? filesArgs : [filesArgs]

                if (files.length) {
                    this.$refs['files-input'].reset()
                    let isValid = true

                    if (typeof this.validator == 'function') {
                        isValid = this.validator(files)
                    }

                    isValid = isValid && (this.multiple || (!this.multiple && files.length == 1))

                    if (isValid) {
                        if (this.validateFilesMaxSize(files)) {
                            this.filesExceededMaxSize = false
                            if (this.validateTypes(files)) {
                                this.files = [...this.files, ...files]
                                this.generateFilesPreviews()
                            } else {
                                this.$notify({
                                    group: 'foo',
                                    type: 'error',
                                    title: this.$t('Projects_notify_error_title30'),
                                    text: this.typeErrorMessage ? this.typeErrorMessage : '',
                                })
                            }
                        } else {
                            // Some file exceeded 20MB
                            this.filesExceededMaxSize = true
                        }
                    } else {
                        this.$notify({
                            group: 'foo',
                            type: 'error',
                            title: this.$t('Projects_notify_error_title31'),
                            text: this.validationMessage ? this.validationMessage : this.$t('Projects_notify_error_text63'),
                        })
                    }
                }
            }
        },

        validateTypes(files) {
            let fileTypesArray = this.accept
                ? this.accept
                      .replace(' ', '')
                      .replace('.', '')
                      .split(',')
                      .map((t) => t.trim())
                : null
            if (fileTypesArray) {
                return files.some((file) => fileTypesArray.some((t) => file.type.toLowerCase().includes(t)))
            } else return true
        },

        generateFilesPreviews() {
            this.files.forEach(async (file, i) => {
                if (file.type.includes('image')) {
                    this.$set(this.previews, i, await CINUtils.toBase64(file))
                } else {
                    this.$set(this.previews, i, null)
                }
            })
        },

        removeFile(index) {
            this.files.splice(index, 1)
            this.previews.splice(index, 1)

            if (!this.previews.length) {
                this.filesExceededMaxSize = false
            }
        },
    },
    mounted() {
        if (this.value && this.value.length) {
            this.files = this.value
            this.generateFilesPreviews()
        }
    },
}
</script>
<style lang="scss" scoped>
#custom-file-dropzone {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    .drop-container {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background: var(--primary-1);
        border-radius: 4px;
        border: 1px dashed var(--secondary-1);
        cursor: pointer;
        p {
            text-align: center;
            b {
                color: var(--accent);
            }
        }

        svg {
            display: none;
        }

        &.minimize {
            height: 40px;
            margin-top: 8px;
            transition: height 0.5s;

            p {
                display: none;
            }

            svg {
                display: initial;
            }
        }
    }

    .files-preview-container {
        width: 0;
        height: 0;
        transition: all 0.7s;
        overflow-x: none;
        overflow-y: auto;
        display: flex;
        flex-wrap: wrap;
        padding-bottom: 4px;
        &.expanded {
            width: 100%;
            height: 100%;
            max-width: 280px;
            transition: all 0.7s;
        }

        .file-name-preview {
            height: 24px;
            display: flex;
            margin: 4px 0;
            &:not(:last-child) {
                margin-right: 4px;
            }

            p {
                border-radius: 4px 0 0 4px;
                background: var(--primary-3);
                width: 100px;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
                text-align: center;
                line-height: 24px;
            }

            .remove-icon {
                background: var(--primary-3);
                border-radius: 0 4px 4px 0;
                height: 24px;
                width: 24px;
                margin-left: 2px;
                display: flex;
                justify-content: center;
                align-items: center;
                cursor: pointer;
            }
        }
    }
}
</style>
